import { Injectable, Injector } from '@angular/core';
import { UserService } from 'src/app/core/services/user.service';
import { BehaviorSubject, filter, map, Observable, of, switchMap, take } from 'rxjs';
import { Channel } from '../interfaces/channel.interface';
import { Tag } from '../interfaces/tag';
import { Company } from '@livechat/shared';
import { AttendanceChannel, Attendant } from '../interfaces/attendance';
import { StartFilters } from 'src/app/core/interfaces/start';
import {
  AVAILABLE_CHANNEL_FILTERS,
  CHANNEL_ICON_MAP,
  CHANNEL_NAME_MAP,
} from '../constants/channel.constant';
import { LocalizationService } from 'src/app/core/services/localization.service';
import { CompanyService } from 'src/app/core/services/company.service';
import { StartService } from 'src/app/core/services/start.service';
import { OpenedByUrlAccessService } from './opened-by-url-access.service';
import { TagParserAdapter } from '../adapters/tag-parser.adapter';
import { AttendanceService } from './attendance.service';
import { StorageUtilService } from 'src/app/core/services/storage-util.service';
import {
  BookingAttendant,
  ExpediaAttendant,
  FacebookAutomationAttendant,
  GoogleAutomationAttendant,
  NotAssignedAttendant,
  WhatsAppWebAttendant,
} from '../constants/attendant.constant';
import { ReloadHandlerService } from '../helpers/reload-handler-v2.helper';
import { FILTER_STATE_PRESET } from '../constants/filter-state-preset.constant';
import {
  FilterOptions,
  FilterState,
} from '../interfaces/attendance-list-filter.interface';
import { FilterArrayParams } from '../../../shared/types';
import { Params } from '@angular/router';
import moment from 'moment';

const FILTER_STORAGE_KEY = 'attendance-filters';

type ResetFilter<R extends keyof FilterOptions> = {
  stateAttribute: FilterArrayParams<FilterState>;
  optionAttribute: R;
  valueProperty: keyof FilterOptions[R][0];
};

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  public filterCount$: Observable<number>;
  private filterStateSub = new BehaviorSubject<FilterState | undefined>(
    undefined
  );
  private filterStateLoadingSub = new BehaviorSubject(false);
  public filterState$ = this.filterStateSub.pipe(
    filter(Boolean),
    filter(() => !this.filterStateLoadingSub.value)
  );
  public isLoading$ = this.filterStateLoadingSub.asObservable();
  private filterOptionsSub = new BehaviorSubject<FilterOptions | undefined>(
    undefined
  );
  public filterOptions$ = this.filterOptionsSub.pipe(
    filter(Boolean),
    filter(() => !this.filterStateLoadingSub.value)
  );
  private fixedFilterAssignedToMeSub = new BehaviorSubject<boolean>(false);

  private localstorageKey = '';

  constructor(
    private userService: UserService,
    private localization: LocalizationService,
    private companyService: CompanyService,
    private tagParserAdapter: TagParserAdapter,
    private attendanceService: AttendanceService,
    private storage: StorageUtilService<FilterState>,
    private reloaderHandler: ReloadHandlerService,
    private openedByUrlAccess: OpenedByUrlAccessService,
    private injector: Injector
  ) {
    this.filterCount$ = this.filterState$.pipe(
      filter(Boolean),
      map((filterState) => this.getFiltersCount(filterState!))
    );
    this.init();
  }

  private get startService(): StartService {
    return this.injector.get(StartService); //TO AVOID CYCLIC DEPENDENCY
  }

  private _filterDropdown?: any;

  public set filterDropdown(filterDropdown: any) {
    this._filterDropdown = filterDropdown;
  }

  private _defaultFilters?: FilterState;

  public get defaultFilters() {
    return this._defaultFilters;
  }

  public get filterState() {
    return this.filterStateSub.value;
  }

  public get filterOptions() {
    return this.filterOptionsSub.value;
  }

  public get fixedFilterAssignedToMe$() {
    return this.fixedFilterAssignedToMeSub.asObservable();
  }

  private _fixedFilterAssignedToMe = false;

  public get fixedFilterAssignedToMe() {
    return this._fixedFilterAssignedToMe;
  }

  public openDropdown() {
    this._filterDropdown?.show();
  }

  public reset(keepFilters?: Partial<FilterState>) {
    if (this.hasSomeCompanyRemoved(keepFilters)) keepFilters = undefined;

    this.filterStateLoadingSub.next(true);
    this.prepareOptions(keepFilters?.companyIds).subscribe((filterOptions) => {
      this.filterStateLoadingSub.next(false);
      this.setOptions(filterOptions);
      const attendant = this.getAttendantForFixedFilterAssigned();
      this.setFilters(
        this.prepareFilters(filterOptions, {
          ...(keepFilters ?? {}),
          ...(keepFilters?.allAttendantsSelected && {
            attendantIds: filterOptions.attendants.map((a) => a.id),
          }),
          ...(keepFilters?.allTagsSelected && {
            tagIds: filterOptions.tags.map((t) => String(t.id)),
          }),
          ...{ assignedAttendant: attendant ? attendant.id.toString() : '' },
        })
      );
    });
  }

  public resetAndKeepFilters() {
    const filtersToKeep: Partial<FilterState> = {
      dateRange: this.filterState?.dateRange,
      lastAttendanceChain: this.filterState?.lastAttendanceChain,
      searchInput: this.filterState?.searchInput,
      freeEntryPoint: this.filterState?.freeEntryPoint,
    };

    if (this.userService.isCurrentUserGlobal()) {
      filtersToKeep.companyIds = this.filterState?.companyIds;
    }

    this.reset(filtersToKeep);
  }

  public getLastAttendanceChain(): boolean {
    return this.filterState?.lastAttendanceChain ?? false;
  }

  public setLastAttendanceChain(value: boolean) {
    this.setFilters({ lastAttendanceChain: value });
  }

  public setCustomDateFilter() {
    const dateRange = this.filterState?.dateRange;
    const customDateRange = {
      ...dateRange,
      label: 'CUSTOM_PERIOD',
    };

    this.setFilters({ dateRange: customDateRange });
    return {dateRange: customDateRange};
  }
  public setLastQuarterDateFilter() {
    const dateNow = moment();
    const lastQuarterStart = moment(dateNow).subtract(3, 'months').startOf('day');

    const dateRange = {
      label: 'LAST_QUARTER',
      from: lastQuarterStart.toISOString(),
      to: dateNow.toISOString()
    };

    this.setFilters({ dateRange });
    return {dateRange};
  }

  public setFilters(filterState: Partial<FilterState>) {
    if (this.filterState?.publicId) {
      filterState.publicId = '';
      this.openedByUrlAccess.clear();
    }
    this.resetCompaniesFilterIfUserNotGlobal(filterState);
    this.resetAttendantsFilterIfUserNotGlobal(filterState);
    this.resetTagsFilterIfUserNotGlobal(filterState);
    this.resetChannelsFilter(filterState);
    this.filterStateSub.next({
      ...this.filterStateSub.value!,
      ...filterState,
    });
    this.persistFilterState(this.filterState!);
  }

  public setOptions(filterOptions: Partial<FilterOptions>) {
    this.filterOptionsSub.next({
      ...this.filterOptionsSub.value!,
      ...filterOptions,
    });
    this._defaultFilters = this.prepareFilters(this.filterOptions!);
  }

  public prepareOptions(companyIds: string[] = []): Observable<FilterOptions> {
    const userCompanyIds = companyIds?.length
      ? companyIds
      : this.getCompanies().map((c) => c.companyId);

    const filterOptions: FilterOptions = {
      companies: this.getCompanies(),
      attendants: this.getDefaultAttendant(),
      tags: [],
      channels: this.getChannelFilters(),
    };

    if (this.userService.isCurrentUserGlobal() && !companyIds?.length)
      return of(filterOptions);

    return this.attendanceService.startFilters(userCompanyIds).pipe(
      map((startFilters: StartFilters) => {
        this._fixedFilterAssignedToMe = !!startFilters.fixedAssignedToMe;
        return {
          ...filterOptions,
          attendants: this.parseAttendants(startFilters),
          tags: this.parseTags(startFilters),
        };
      })
    );
  }

  public prepareFilters(
    filterOptions: FilterOptions,
    filtersToKeep?: Partial<FilterState> | null
  ): FilterState {
    let result = JSON.parse(JSON.stringify(FILTER_STATE_PRESET));

    result.channelIds = filterOptions.channels.map((c) => c.id);

    if (!this.userService.isCurrentUserGlobal()) {
      result.attendantIds = filterOptions.attendants.map((a) => a.id);
      result.companyIds = filterOptions.companies.map((c) => c.companyId);
      result.tagIds = filterOptions.tags.map((t) => t.id) as string[]; //TODO: transformar em string em todos os locais ao invés de string | number
    }

    if (filtersToKeep) {
      result = {
        ...result,
        ...filtersToKeep,
      };
    }

    if (this._fixedFilterAssignedToMe) {
      result.attendantIds = [];
    }

    return result;
  }

  public shouldShowAllItems() {
    return this.userService.isCurrentUserGlobal();
  }

  resetTagsFilterIfUserNotGlobal(filterState: Partial<FilterState>) {
    if (this.userService.isCurrentUserGlobal()) return;
    this.resetArrayFilterAttribute(filterState, {
      stateAttribute: 'tagIds',
      optionAttribute: 'tags',
      valueProperty: 'id',
    });
  }

  resetChannelsFilter(filterState: Partial<FilterState>) {
    if (this.userService.isCurrentUserGlobal()) return;
    this.resetArrayFilterAttribute(filterState, {
      stateAttribute: 'channelIds',
      optionAttribute: 'channels',
      valueProperty: 'id',
    });
  }

  getAttendantForCurrentUser() {
    const currentUser = this.userService.getCurrentUser();
    return (
      this.filterOptions &&
      this.filterOptions.attendants.find(
        (att) => att.userReferenceId === currentUser.id.toString()
      )
    );
  }

  private getAttendantForFixedFilterAssigned() {
    const attendant =
      this.fixedFilterAssignedToMe && this.getAttendantForCurrentUser();
    this.fixedFilterAssignedToMeSub.next(this.fixedFilterAssignedToMe);
    return attendant;
  }

  private resetCompaniesFilterIfUserNotGlobal(
    filterState: Partial<FilterState>
  ) {
    if (this.userService.isCurrentUserGlobal()) return;
    this.resetArrayFilterAttribute(filterState, {
      stateAttribute: 'companyIds',
      optionAttribute: 'companies',
      valueProperty: 'companyId',
    });
  }

  private resetAttendantsFilterIfUserNotGlobal(
    filterState: Partial<FilterState>
  ) {
    if (this.userService.isCurrentUserGlobal()) return;
    this.resetArrayFilterAttribute(filterState, {
      stateAttribute: 'attendantIds',
      optionAttribute: 'attendants',
      valueProperty: 'id',
    });
  }

  private resetArrayFilterAttribute<R extends keyof FilterOptions>(
    filterState: Partial<FilterState>,
    resetFilter: ResetFilter<R>
  ) {
    const { stateAttribute, optionAttribute, valueProperty } = resetFilter;

    if (
      this.filterOptions &&
      !!stateAttribute &&
      !!filterState[stateAttribute] &&
      !filterState[stateAttribute]?.length
    )
      filterState[stateAttribute] = this.filterOptions[optionAttribute].map(
        (option) => (option as FilterOptions[R][0])[valueProperty]
      ) as (typeof filterState)[FilterArrayParams<FilterState>];
  }

  private init() {
    const userId = this.userService.getCurrentUser()?.id;
    this.localstorageKey = `${FILTER_STORAGE_KEY}_${userId}`;
    const filterStorage = this.retrieveFilterState();

    // Global users' companies list lives in memory only (too large to persist
    // to localStorage), so it's only reliably populated once /start/livechat
    // responds. Non-global users keep the immediate, localStorage-backed path.
    const ready$ = this.userService.isCurrentUserGlobal()
      ? this.startService.sessionSettled$
      : of(undefined);

    ready$
      .pipe(
        switchMap(() => this.reloaderHandler.reloadHandler()),
        take(1)
      )
      .subscribe((params) => {
        const isLastUser = this.isLastUser(params);

        if (params && !isLastUser) {
          const filters: Partial<FilterState> = {};
          const UUID_REGEX =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          const validPublicId = UUID_REGEX.test(params['publicId'])
            ? params['publicId']
            : null;

          if (validPublicId) {
            filters.publicId = validPublicId;

            if (!this.userService.isCurrentUserGlobal()) {
              filters.companyIds = filterStorage?.companyIds
                ? [...filterStorage.companyIds]
                : [];

              if (
                params['companyId'] &&
                !filters.companyIds.includes(params['companyId'])
              ) {
                filters.companyIds.push(params['companyId']);
              }
            } else {
              if (params['companyId']) {
                filters.companyIds = [params['companyId']];
              }
            }
          }

          if (
            params['companyId'] &&
            (!filters.companyIds || filters.companyIds.length === 0)
          ) {
            filters.companyIds = [params['companyId']];
          }

          if (validPublicId && params['companyId']) {
            this.openedByUrlAccess.register(params['companyId']);
          }

          this.reset(filters);
        } else {
          const filters = this.retrieveFilterState();
          if (filters) {
            filters.publicId = '';
          }
          this.reset(filters);
        }
      });
  }

  private isLastUser(params: Params | null) {
    if (params) {
      const hashUserToCompare = this.userService.generateHashUser();
      return params['token'] === hashUserToCompare;
    }

    return false;
  }

  private hasSomeCompanyRemoved(filters?: Partial<FilterState>): boolean {
    if (this.userService.isCurrentUserGlobal()) return false;

    const filterCompanies = filters?.companyIds || [];
    const userCompaniesIds = this.getCompanies().map((c) => c.companyId);

    return filterCompanies.some(
      (fc) => !userCompaniesIds.some((uc) => uc === fc)
    );
  }

  private getFiltersCount(filterState: FilterState) {
    let count = 0;
    const defaultFilters = this.defaultFilters!;
    if (filterState.companyIds?.length !== defaultFilters.companyIds?.length)
      count++;
    if (
      filterState.attendantIds?.length !==
        defaultFilters.attendantIds?.length &&
      !this._fixedFilterAssignedToMe
    )
      count++;
    if (filterState.tagIds?.length !== defaultFilters.tagIds?.length) count++;
    if (filterState.channelIds?.length !== defaultFilters.channelIds?.length)
      count++;
    if (this.checkIfAttendanceStatusHasChanged(filterState, defaultFilters))
      count++;
    if (this.checkIfMessageStatusHasChanged(filterState, defaultFilters))
      count++;
    if (this.checkIfAttendanceResponseHasChanged(filterState, defaultFilters))
      count++;
    return count;
  }

  private retrieveFilterState(): FilterState | undefined {
    return this.storage.getItem(this.localstorageKey);
  }

  private persistFilterState(filterState: FilterState) {
    this.storage.setItem(this.localstorageKey, {
      ...filterState,
      allAttendantsSelected:
        filterState.attendantIds.length ===
        this.filterOptions?.attendants.length,
      allTagsSelected:
        filterState.tagIds.length === this.filterOptions?.tags.length,
    });
  }

  private checkIfAttendanceStatusHasChanged(
    filterState: FilterState,
    defaultFilters: FilterState
  ) {
    return (
      filterState.attendanceStatus.archived !==
        defaultFilters.attendanceStatus.archived ||
      filterState.attendanceStatus.closed !==
        defaultFilters.attendanceStatus.closed ||
      filterState.attendanceStatus.opened !==
        defaultFilters.attendanceStatus.opened
    );
  }

  private checkIfMessageStatusHasChanged(
    filterState: FilterState,
    defaultFilters: FilterState
  ) {
    return (
      filterState.messageStatus.read !== defaultFilters.messageStatus.read ||
      filterState.messageStatus.unread !== defaultFilters.messageStatus.unread
    );
  }

  private checkIfAttendanceResponseHasChanged(
    filterState: FilterState,
    defaultFilters: FilterState
  ) {
    return filterState.attendanceResponse !== defaultFilters.attendanceResponse;
  }

  private getChannelFilters(): Channel[] {
    return Object.values(AttendanceChannel)
      .filter((attedanceChannelKey) =>
        AVAILABLE_CHANNEL_FILTERS.includes(attedanceChannelKey)
      )
      .map((attedanceChannelKey) => ({
        id: attedanceChannelKey,
        name: this.localization.key(
          CHANNEL_NAME_MAP[attedanceChannelKey as AttendanceChannel]
        ),
        icon: CHANNEL_ICON_MAP[attedanceChannelKey as AttendanceChannel],
      })) as Channel[];
  }

  private getCompanies(): Company[] {
    return (this.companyService?.getCompanies() || []).filter(
      (c) => c.companyName && c.companyId
    );
  }

  private parseTags(startFilters?: StartFilters): Tag[] {
    if (!startFilters?.tags) return [];
    return this.tagParserAdapter.adapt(startFilters.tags);
  }

  private parseAttendants(startFilters: StartFilters): Attendant[] {
    const attendants = startFilters?.attendants;
    const attendantsOptions: Attendant[] = [];
    if (attendants) {
      this.addAttendantWithSameUserId(attendants, attendantsOptions);
      this.addAttendantsOrderedByName(attendants, attendantsOptions);
    }

    return [...attendantsOptions, ...this.getDefaultAttendant()];
  }

  private addAttendantWithSameUserId(
    attendants: Attendant[],
    attendantsOptions: Attendant[]
  ) {
    const user = this.userService.getCurrentUser();
    const sameUserIndex = attendants?.findIndex(
      (attendant) => attendant.userReferenceId === user.id.toString()
    );

    if (sameUserIndex >= 0 && attendants?.length) {
      const [attendant] = attendants.splice(sameUserIndex, 1);
      attendantsOptions.push({
        ...attendant,
        name: this.localization.key('YOU'),
      });
    }
  }

  private addAttendantsOrderedByName(
    attendants: Attendant[] = [],
    attendantsOptions: Attendant[]
  ) {
    const attendantsOrderedByName = attendants.sort((a, b) =>
      (a?.name || '').localeCompare(b?.name || '')
    );

    attendantsOptions.push(...attendantsOrderedByName);
  }

  private getDefaultAttendant(): Attendant[] {
    return [
      {
        id: NotAssignedAttendant,
        name: this.localization.key('ROBOT') as string,
        userReferenceId: '',
      },
      {
        id: WhatsAppWebAttendant,
        name: this.localization.key('ATTENDANT_WHATSAPP_WEB') as string,
        userReferenceId: '',
      },
      {
        id: BookingAttendant,
        name: this.localization.key('ATTENDANT_BOOKING') as string,
        userReferenceId: '',
      },
      {
        id: ExpediaAttendant,
        name: this.localization.key('ATTENDANT_EXPEDIA') as string,
        userReferenceId: '',
      },
      {
        id: FacebookAutomationAttendant,
        name: this.localization.key('ATTENDANT_FACEBOOK') as string,
        userReferenceId: '',
      },
      {
        id: GoogleAutomationAttendant,
        name: this.localization.key('ATTENDANT_GOOGLE') as string,
        userReferenceId: '',
      },
    ];
  }

  getAttendantsFromCompanies() {
    return this.prepareOptions(this.filterState?.companyIds);
  }
}
